import PageHeading from '../components/PageHeading';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate, Link } from 'react-router-dom';
import { Lock, Eye, EyeOff, AlertCircle, CheckCircle, Loader2 } from 'lucide-react';
import { updatePassword } from '../api/user';

export default function ChangePassword() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.user);

  // 表单状态
  const [formData, setFormData] = useState({
    newPassword: '',
    confirmPassword: ''
  });

  // UI状态
  const [showPasswords, setShowPasswords] = useState({
    new: false,
    confirm: false
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);


  // 处理输入变化
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    setError('');
  };

  // 切换密码可见性
  const togglePasswordVisibility = (field) => {
    setShowPasswords(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  // 验证表单
  const validateForm = () => {
    if (!formData.newPassword) {
      setError(t('changePassword.errors.newPasswordRequired'));
      return false;
    }

    if (formData.newPassword !== formData.confirmPassword) {
      setError(t('changePassword.errors.passwordMismatch'));
      return false;
    }

    return true;
  };

  // 提交表单
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    setLoading(true);
    setError('');

    try {
      // 获取当前用户的phoneNumbers (email或uid)
      const phoneNumbers = user?.email || user?.uid;
      
      if (!phoneNumbers) {
        setError(t('changePassword.errors.userNotFound'));
        return;
      }

      // 调用API修改密码
      const response = await updatePassword({
        phoneNumbers: phoneNumbers,
        password: formData.newPassword
      });

      if (response.code === 1) {
        setSuccess(true);
        setTimeout(() => {
          navigate('/settings');
        }, 3000);
      } else {
        setError(response.message || t('changePassword.errors.changeFailed'));
      }
    } catch (error) {
      console.error('修改密码失败:', error);
      setError(error.response?.data?.message || t('changePassword.errors.changeFailed'));
    } finally {
      setLoading(false);
    }
  };

  if (!user) {
    return <div className="page-shell max-w-2xl"><PageHeading Icon={Lock} eyebrow="SECURITY · 账号安全" title="修改密码" description="请先登录需要修改密码的网站账号。" /><div className="page-card text-center"><p className="text-muted mb-6">登录后即可管理密码与查看订单。</p><Link to="/settings" className="brand-purchase">前往登录</Link></div></div>;
  }

  // 如果修改成功，显示成功页面
  if (success) {
    return (
      <div className="min-h-screen bg-canvas py-8 px-4">
        <div className="max-w-md mx-auto">
          <div className="bg-white rounded-2xl shadow-panel p-8">
            <div className="flex flex-col items-center">
              <div className="w-16 h-16 bg-gray-500 rounded-full flex items-center justify-center mb-4">
                <CheckCircle className="w-10 h-10 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-ink mb-2">
                {t('changePassword.success.title')}
              </h2>
              <p className="text-muted text-center">
                {t('changePassword.success.message')}
              </p>
              <p className="text-sm text-muted mt-4">
                {t('changePassword.success.redirecting')}
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-canvas py-8 px-4">
      <div className="max-w-md mx-auto">
        {/* 页面标题 */}
        <PageHeading Icon={Lock} eyebrow="SECURITY · 账号安全" title={t('changePassword.title')} description={t('changePassword.subtitle')} />

        {/* 表单 */}
        <div className="bg-white rounded-2xl shadow-panel p-8">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* 错误提示 */}
            {error && (
              <div className="bg-canvas border border-line rounded-lg p-4">
                <div className="flex items-center gap-2">
                  <AlertCircle className="w-5 h-5 text-muted flex-shrink-0" />
                  <span className="text-ink text-sm">{error}</span>
                </div>
              </div>
            )}


            {/* 新密码 */}
            <div>
              <label htmlFor="newPassword" className="block text-sm font-medium text-ink mb-2">
                {t('changePassword.form.newPassword')}
              </label>
              <div className="relative">
                <input
                  type={showPasswords.new ? 'text' : 'password'}
                  id="newPassword"
                  name="newPassword"
                  value={formData.newPassword}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 pr-12 border border-line rounded-lg focus:ring-2 focus:ring-brand focus:border-transparent"
                  placeholder={t('changePassword.form.newPasswordPlaceholder')}
                  disabled={loading}
                />
                <button
                  type="button"
                  onClick={() => togglePasswordVisibility('new')}
                  aria-label={showPasswords.new ? '隐藏新密码' : '显示新密码'}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted hover:text-ink"
                >
                  {showPasswords.new ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            {/* 确认新密码 */}
            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-ink mb-2">
                {t('changePassword.form.confirmPassword')}
              </label>
              <div className="relative">
                <input
                  type={showPasswords.confirm ? 'text' : 'password'}
                  id="confirmPassword"
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 pr-12 border border-line rounded-lg focus:ring-2 focus:ring-brand focus:border-transparent"
                  placeholder={t('changePassword.form.confirmPasswordPlaceholder')}
                  disabled={loading}
                />
                <button
                  type="button"
                  onClick={() => togglePasswordVisibility('confirm')}
                  aria-label={showPasswords.confirm ? '隐藏确认密码' : '显示确认密码'}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted hover:text-ink"
                >
                  {showPasswords.confirm ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              {formData.confirmPassword && formData.newPassword !== formData.confirmPassword && (
                <p className="mt-1 text-sm text-muted">
                  {t('changePassword.errors.passwordMismatch')}
                </p>
              )}
            </div>

            {/* 提交按钮 */}
            <div className="flex gap-4">
              <button
                type="button"
                onClick={() => navigate('/settings')}
                className="flex-1 px-6 py-3 border border-line text-ink rounded-lg font-semibold hover:bg-canvas transition-colors"
                disabled={loading}
              >
                {t('common.cancel')}
              </button>
              <button
                type="submit"
                className="flex-1 bg-brand text-white py-3 rounded-lg font-semibold hover:bg-brand-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    {t('common.processing')}
                  </>
                ) : (
                  t('changePassword.form.submit')
                )}
              </button>
            </div>
          </form>

        </div>
      </div>
    </div>
  );
}